/**
 * VAT Validation Service
 * Handles VAT number validation and tax rate determination
 */

import { apiService } from './api';

export interface VatValidationRequest {
  vat_number?: string | null;
  customer_country: string;
}

export interface VatValidationResponse {
  vat_number: string | null;
  customer_country: string;
  country_code: string;
  vat_number_normalized: string;
  is_valid: boolean;
  checked: boolean;
  tax_rate: number;
  failure_reason: string | null;
  request_identifier: string | null;
}

export interface BulkVatValidationItem {
  customer_id?: number | null;
  contract_id?: string | null;
}

export interface BulkVatValidationRequest {
  items: BulkVatValidationItem[];
}

export interface BulkVatValidationResult {
  customer_id: number | null;
  contract_id: string | null;
  vat_number: string | null;
  customer_country: string;
  country_code: string;
  vat_number_normalized: string;
  is_valid: boolean;
  checked: boolean;
  tax_rate: number;
  failure_reason: string | null;
  request_identifier: string | null;
}

export interface BulkVatValidationResponse {
  results: BulkVatValidationResult[];
}

class VatValidationService {
  /**
   * Validate a single VAT number
   */
  async validateVat(vatNumber: string | null, customerCountry: string): Promise<VatValidationResponse> {
    const request: VatValidationRequest = {
      vat_number: vatNumber || null,
      customer_country: customerCountry
    };
    
    try {
      const response = await apiService.post<VatValidationResponse>('/api/v1/vat/validate', request);
      return response;
    } catch (error) {
      console.error('VAT validation failed:', error);
      return {
        vat_number: vatNumber || null,
        customer_country: customerCountry,
        country_code: customerCountry.toUpperCase(),
        vat_number_normalized: vatNumber ? vatNumber.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') : '',
        is_valid: false,
        checked: false,
        tax_rate: customerCountry.toUpperCase() === 'DE' ? 19.0 : 0.0,
        failure_reason: 'Validation service unavailable',
        request_identifier: null
      };
    }
  }
  
  /**
   * Validate VAT for a specific customer
   */
  async validateCustomerVat(customerId: number): Promise<VatValidationResponse> {
    try {
      const response = await apiService.post<VatValidationResponse>(`/api/v1/vat/validate-customer/${customerId}`);
      return response;
    } catch (error) {
      console.error('Customer VAT validation failed:', error);
      return {
        vat_number: null,
        customer_country: 'DE',
        country_code: 'DE',
        vat_number_normalized: '',
        is_valid: false,
        checked: false,
        tax_rate: 19.0,
        failure_reason: 'Validation service unavailable',
        request_identifier: null
      };
    }
  }
  
  /**
   * Bulk validate VAT numbers for multiple customers/contracts
   */
  async bulkValidateVat(items: BulkVatValidationItem[]): Promise<BulkVatValidationResponse> {
    const request: BulkVatValidationRequest = {
      items: items
    };
    
    try {
      const response = await apiService.post<BulkVatValidationResponse>('/api/v1/vat/bulk-validate', request);
      return response;
    } catch (error) {
      console.error('Bulk VAT validation failed:', error);
      return {
        results: items.map(item => ({
          customer_id: item.customer_id || null,
          contract_id: item.contract_id || null,
          vat_number: null,
          customer_country: 'Unknown',
          country_code: 'XX',
          vat_number_normalized: '',
          is_valid: false,
          checked: false,
          tax_rate: 19.0,
          failure_reason: 'Validation service unavailable',
          request_identifier: null
        }))
      };
    }
  }
  
  /**
   * Check VAT validation status for contract lines (bulk operation)
   */
  async checkContractLinesVat(contractIds: string[]): Promise<BulkVatValidationResponse> {
    const items: BulkVatValidationItem[] = contractIds.map(contractId => ({
      contract_id: contractId
    }));
    
    const request: BulkVatValidationRequest = {
      items: items
    };
    
    try {
      const response = await apiService.post<BulkVatValidationResponse>('/api/v1/vat/check-contract-lines', request);
      return response;
    } catch (error) {
      console.error('Contract lines VAT check failed:', error);
      return {
        results: contractIds.map(contractId => ({
          customer_id: null,
          contract_id: contractId,
          vat_number: null,
          customer_country: 'Unknown',
          country_code: 'XX',
          vat_number_normalized: '',
          is_valid: false,
          checked: false,
          tax_rate: 19.0,
          failure_reason: 'Validation service unavailable',
          request_identifier: null
        }))
      };
    }
  }
  
  /**
   * Determine tax rate based on customer country (simplified logic)
   */
  determineTaxRate(customerCountry: string, vatValid: boolean = false): number {
    // 0% tax if customer is outside Germany
    if (customerCountry.toUpperCase() !== 'DE') {
      return 0.0;
    }
    
    // 19% tax for German customers
    return 19.0;
  }
}

export const vatValidationService = new VatValidationService();
